import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export type StatTone = 'green' | 'trinidad' | 'gold' | 'neutral';

let nextId = 0;

/**
 * A number with a label, in cnt-landing's dashboard stat-tile style
 * (libs/ui-cnt/src/lib/stat-tile): icon badge, orange label, large value, an
 * optional sparkline of recent values and a trend caption.
 */
@Component({
  selector: 'admin-stat-tile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tile" [class]="'tile tone-' + tone">
      <div class="head">
        <span class="badge" aria-hidden="true"
          ><span class="material-symbols-outlined icon-filled">{{ icon }}</span></span
        >
        <span class="label">{{ label }}</span>
      </div>
      <div class="value">{{ display }}</div>
      @if (sparkLine) {
        <svg class="spark" viewBox="0 0 80 22" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient [attr.id]="gradientId" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="currentColor" stop-opacity="0.18"></stop>
              <stop offset="100%" stop-color="currentColor" stop-opacity="0"></stop>
            </linearGradient>
          </defs>
          <path [attr.d]="sparkArea" [attr.fill]="'url(#' + gradientId + ')'"></path>
          <polyline
            [attr.points]="sparkLine"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></polyline>
        </svg>
      }
      @if (trend) {
        <div class="trend" [class.up]="trendDirection === 'up'" [class.down]="trendDirection === 'down'">
          {{ trend }}
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .tile {
        --tone: var(--cnt-ink, #222);
        --tint: rgba(34, 34, 34, 0.06);
        position: relative;
        height: 100%;
        padding: 18px 20px;
        overflow: hidden;
        border: 1px solid var(--cnt-line, rgba(34, 34, 34, 0.08));
        border-radius: 1rem;
        background: #fff;
        box-shadow: var(--cnt-card-shadow, 0 4px 16px rgba(0, 0, 0, 0.03));
        transition:
          transform 0.3s ease,
          box-shadow 0.3s ease;
      }
      @media (hover: hover) and (prefers-reduced-motion: no-preference) {
        .tile:hover {
          transform: translateY(-2px);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.07);
        }
      }
      .tone-green {
        --tone: var(--cnt-green, #295d42);
        --tint: rgba(41, 93, 66, 0.1);
      }
      .tone-trinidad {
        --tone: var(--cnt-trinidad, #e3530d);
        --tint: rgba(227, 83, 13, 0.1);
      }
      .tone-gold {
        --tone: #b3760e;
        --tint: rgba(251, 215, 132, 0.3);
      }
      .head {
        position: relative;
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
      }
      .badge {
        display: inline-flex;
        flex: none;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 9999px;
        background: var(--tint);
        color: var(--tone);
      }
      .badge .material-symbols-outlined {
        font-size: 17px;
      }
      .label {
        color: var(--cnt-trinidad, #e3530d);
        font-size: 0.62rem;
        font-weight: 700;
        line-height: 1.2;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .value {
        position: relative;
        color: var(--tone);
        font-size: clamp(1.6rem, 2.6vw, 2.25rem);
        font-weight: 700;
        line-height: 1;
        letter-spacing: -0.02em;
        overflow-wrap: anywhere;
      }
      .tone-neutral .value,
      .tone-gold .value {
        color: var(--cnt-ink, #222);
      }
      .spark {
        position: relative;
        display: block;
        width: 100%;
        height: 22px;
        margin-top: 12px;
        color: var(--tone);
      }
      .trend {
        position: relative;
        margin-top: 8px;
        color: var(--cnt-muted, #707070);
        font-size: 0.75rem;
      }
      .trend.up {
        color: var(--cnt-green, #295d42);
      }
      .trend.down {
        color: var(--cnt-danger, #b3261e);
      }
    `,
  ],
})
export class AdminStatTileComponent {
  @Input({ required: true }) label = '';
  @Input({ required: true }) value: string | number = '';
  /** Material Symbols name. */
  @Input() icon = 'insights';
  @Input() tone: StatTone = 'neutral';
  @Input() trend = '';
  @Input() trendDirection: 'up' | 'down' | 'flat' = 'flat';

  readonly gradientId = `admin-spark-${++nextId}`;

  /** Numbers with thousands separators (1,070,160); text as given. */
  get display(): string {
    return typeof this.value === 'number' ? this.value.toLocaleString('en-US') : this.value;
  }
  sparkLine = '';
  sparkArea = '';

  /** Recent values, oldest first, drawn as a small line. */
  @Input() set spark(values: number[] | undefined) {
    const points = sparkPoints(values ?? []);
    this.sparkLine = points.map(([x, y]) => `${x},${y}`).join(' ');
    this.sparkArea = points.length ? `M${points.map(([x, y]) => `${x},${y}`).join(' L')} L80,22 L0,22 Z` : '';
  }
}

/** Values scaled into the 80×22 sparkline box (2px padding top and bottom). */
export function sparkPoints(values: number[]): [number, number][] {
  if (values.length < 2) return [];
  const min = Math.min(...values);
  const range = Math.max(...values) - min || 1;
  const step = 80 / (values.length - 1);
  return values.map((v, i) => [round(i * step), round(20 - ((v - min) / range) * 18)]);
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}
